import {
  API_BASE_URL,
  apiDelete,
  apiGet,
  apiGetOptional,
  apiPatch,
  apiPost,
  apiPut,
} from '@/api/http';

import type { AppNavigate } from '@/hooks/useUrlState';
import type { CompanyAgreement } from '@/types/company';
import type { JobListAssignment } from '@/types/job';
import type {
  Recruiter,
  RecruiterAgreementActionPayload,
  RecruiterInvitePayload,
  RecruiterListItem,
  RecruiterProfile,
  RecruiterUpdatePayload,
} from '@/types/recruiter';
import type { CursorListingApiPayload } from '@/UI/filteredListing';
import type { AxiosRequestConfig } from 'axios';

export type RecruiterCompanyPayload = {
  name?: string;
  default_language?: string;
  default_currency?: string;
  is_virtual?: boolean;
  about_company?: string;
  company_banner_image?: string;
};

export type RecruiterAgreementPayload =
  FormData | Partial<CompanyAgreement> | RecruiterAgreementActionPayload;

export type AgencyFilterCounts = {
  specializations: { id: string; name: string; count: number }[];
  performance: { all: number; '3': number; '4': number; '5': number };
  ownership: { own: number; marketplace: number };
  workedWith: number;
  termsStatus: {
    accepted: number;
    pending: number;
    declined: number;
    not_invited: number;
    without_contract: number;
  };
  feeType: { type: string; count: number }[];
  employmentType: { id: string; name: string; count: number }[];
  listIds: { id: string; name: string; count: number }[];
  listNotInList?: number;
  assignmentStatus?: {
    assigned: number;
    scheduled: number;
    not_assigned: number;
  };
  agencyCompanies?: { id: string; name: string; count: number }[];
  isNew?: number;
};

export const getAllRecruiters = (query?: string, config?: AxiosRequestConfig) =>
  apiGet<CursorListingApiPayload<Recruiter>>(
    `${API_BASE_URL}recruiters/${query ?? ''}`,
    config,
  );

export type RecruitersFilterCountsResponse = {
  domain: { id: string; count: number }[];
  location: { id: string; count: number }[];
};

export const getRecruitersFilterCounts = (params?: URLSearchParams) => {
  const query = params?.toString();
  const suffix = query ? `?${query}` : '';
  return apiGet<RecruitersFilterCountsResponse>(
    `${API_BASE_URL}recruiters/filter-counts/${suffix}`,
  );
};

export const getRecruitersSelectedForJob = ({
  query,
  job_id,
  params,
}: {
  query?: string;
  job_id?: string | null;
  params?: URLSearchParams;
}) => {
  const jobTier = query ?? '';
  const jobIdPart = job_id ? `&job_id=${job_id}` : '';
  const extraParams = params ? `&${params.toString()}` : '';
  const url = `${API_BASE_URL}recruiters/selected/?job_tier=${encodeURIComponent(
    jobTier,
  )}&selected=true${jobIdPart}${extraParams}`;
  return apiGet<CursorListingApiPayload<Recruiter, AgencyFilterCounts>>(url);
};

export const getRecruitersForRecruiterManagement = (
  ownRecruiter: boolean,
  params?: URLSearchParams,
) => {
  const query = new URLSearchParams();
  query.set('own_recruiter', ownRecruiter ? 'true' : 'false');
  if (params) {
    params.forEach((value, key) => {
      query.set(key, value);
    });
  }
  return apiGet<CursorListingApiPayload<Recruiter>>(
    `${API_BASE_URL}recruiters/recruiter-management/?${query.toString()}`,
  );
};

export const getRecruiterManagementFilterCounts = (
  ownRecruiter: boolean,
  params?: URLSearchParams,
) => {
  const query = new URLSearchParams();
  query.set('own_recruiter', ownRecruiter ? 'true' : 'false');
  if (params) {
    params.forEach((value, key) => {
      query.set(key, value);
    });
  }
  return apiGet<AgencyFilterCounts>(
    `${API_BASE_URL}recruiters/recruiter-management/filter-counts/?${query.toString()}`,
  );
};

export type RecruiterListAggregateStats = {
  count: number;
  hired: number;
  in_process: number;
  uploaded: number;
  assigned_count?: number;
};

export const getSelectedRecruitersListAggregates = ({
  query,
  job_id,
  params,
}: {
  query?: string;
  job_id?: string | null;
  params?: URLSearchParams;
}) => {
  const jobTier = query ?? '';
  const jobIdPart = job_id ? `&job_id=${job_id}` : '';
  const extraParams = params ? `&${params.toString()}` : '';
  const url = `${API_BASE_URL}recruiters/selected/list-aggregates/?job_tier=${encodeURIComponent(
    jobTier,
  )}&selected=true${jobIdPart}${extraParams}`;
  return apiGet<Record<string, RecruiterListAggregateStats>>(url);
};

export const getRecruiterManagementListAggregates = (
  params?: URLSearchParams,
) => {
  const query = new URLSearchParams();
  query.set('own_recruiter', 'true');
  if (params) {
    params.forEach((value, key) => {
      query.set(key, value);
    });
  }
  return apiGet<Record<string, RecruiterListAggregateStats>>(
    `${API_BASE_URL}recruiters/recruiter-management/list-aggregates/?${query.toString()}`,
  );
};

export const getSelectedRecruitersFilterCounts = ({
  query,
  job_id,
  params,
}: {
  query?: string;
  job_id?: string | null;
  params?: URLSearchParams;
}) => {
  const jobTier = query ?? '';
  const jobIdPart = job_id ? `&job_id=${job_id}` : '';
  const extraParams = params ? `&${params.toString()}` : '';
  const url = `${API_BASE_URL}recruiters/selected/filter-counts/?job_tier=${encodeURIComponent(
    jobTier,
  )}&selected=true${jobIdPart}${extraParams}`;
  return apiGet<AgencyFilterCounts>(url);
};

export type PreviouslySelectedRecruitersPayload = {
  results?: string[];
};

export const getPreviouslySelectedRecruiters = (query?: string) =>
  apiGet<PreviouslySelectedRecruitersPayload>(
    `${API_BASE_URL}recruiters/previously-selected/?job_tier=${encodeURIComponent(
      query ?? '',
    )}`,
  );

export const getRecruiterCompanyAgreements = () =>
  apiGet<CompanyAgreement[] | { results: CompanyAgreement[] }>(
    `${API_BASE_URL}recruiters-agreement/`,
  );

export const getRecruiterCompanyAgreementsPage = (params: URLSearchParams) =>
  apiGet<CursorListingApiPayload<CompanyAgreement>>(
    `${API_BASE_URL}recruiters-agreement/?${params.toString()}`,
  );

export const getRecruiterCompanyAgreement = (
  recruiterCompanyAgreementId: string,
) =>
  apiGet<CompanyAgreement>(
    `${API_BASE_URL}recruiters-agreement/${recruiterCompanyAgreementId}/`,
  );

export const getRecruiterCompanyAgreementByCompany = (companyId: string) =>
  apiGet<CompanyAgreement[]>(`${API_BASE_URL}recruiters-agreement/`, {
    params: { company: companyId },
  });

export const patchRecruiterCompanyAgreement = (
  id: string,
  payload: RecruiterAgreementPayload,
) =>
  apiPatch<CompanyAgreement>(
    `${API_BASE_URL}recruiters-agreement/${id}/`,
    payload,
  );

export const forkRecruiterAgreementToTemplate = (
  id: string,
  payload: FormData,
) =>
  apiPost<CompanyAgreement>(
    `${API_BASE_URL}recruiters-agreement/${id}/fork-to-template/`,
    payload,
  );

export const deleteRecruiterCompanyAgreement = ({
  recruiter_id,
  client_company_id,
  force,
}: {
  recruiter_id: string;
  client_company_id: string;
  force?: boolean;
}) =>
  apiDelete(`${API_BASE_URL}recruiters-agreement/delete/`, {
    params: {
      recruiter_id,
      client_company_id,
      ...(force ? { force: 'true' } : {}),
    },
  });

export const sendInviteToRecruiters = ({
  company_name,
  selected_recruiters = [],
  loaded_recruiters = [],
  recruiters_to_remove,
  job_id,
  scheduled_dates,
}: {
  company_name: string;
  selected_recruiters?: string[];
  loaded_recruiters?: string[];
  recruiters_to_remove?: string[];
  job_id: string;
  scheduled_dates?: Record<string, string>;
}) =>
  apiPost(`${API_BASE_URL}recruiters/invite-list/`, {
    company_name,
    selected_recruiters,
    ...(recruiters_to_remove !== undefined
      ? { recruiters_to_remove }
      : { loaded_recruiters }),
    job_id,
    ...(scheduled_dates &&
      Object.keys(scheduled_dates).length > 0 && { scheduled_dates }),
  });

export const inviteOwnSupplier = (reqBody: FormData) =>
  apiPost(`${API_BASE_URL}recruiters/invite-own/`, reqBody);

export const createOwnSupplier = (reqBody: FormData) =>
  apiPost(`${API_BASE_URL}recruiters/create-own/`, reqBody);

export const getRecruiter = (id: string, navigate?: AppNavigate) =>
  apiGetOptional<Recruiter>(
    `${API_BASE_URL}recruiters/${id}/`,
    undefined,
    navigate,
  );

export const editRecruiter = (id: string, info: RecruiterUpdatePayload) =>
  apiPut<Recruiter>(`${API_BASE_URL}recruiters/${id}/`, info);

export const sendRecruiterInvitation = async (info: RecruiterInvitePayload) => {
  const payload = { ...info };
  const company =
    payload.newCompany &&
    (await createRecruiterCompany({ name: payload.newCompany }));
  if (payload.newCompany && company) {
    payload.company = company.id;
  }
  delete payload.newCompany;
  return apiPost<Recruiter>(`${API_BASE_URL}recruiters/invite/`, payload);
};

export const getRecruiterInfo = () =>
  apiGet<Recruiter>(`${API_BASE_URL}recruiters/me/`);

export const updateRecruiter = (info: RecruiterUpdatePayload | FormData) =>
  apiPatch<Recruiter>(`${API_BASE_URL}recruiters/me/`, info);

export const acceptRecruiterTerms = (
  agreementId: string,
  info: RecruiterAgreementPayload,
) =>
  apiPost(`${API_BASE_URL}recruiters-agreement/${agreementId}/accept/`, info);

export const declineRecruiterTerms = (
  agreementId: string,
  info: RecruiterAgreementPayload,
) =>
  apiPost(`${API_BASE_URL}recruiters-agreement/${agreementId}/decline/`, info);

export const getRecruiterAgreementVersions = (agreementId: string) =>
  apiGet<CompanyAgreement[]>(
    `${API_BASE_URL}recruiters-agreement/${agreementId}/versions/`,
  );

const createRecruiterCompany = (info: RecruiterCompanyPayload) =>
  apiPost<{ id: string; name: string }>(
    `${API_BASE_URL}recruiter-companies/`,
    info,
  );

export const getRecruitersCompanyList = () =>
  apiGet<{ id: string; name: string }[]>(`${API_BASE_URL}recruiter-companies/`);

export const updateRecruiterCompany = (
  companyId: string,
  data: RecruiterCompanyPayload,
) => apiPatch(`${API_BASE_URL}recruiter-companies/${companyId}/`, data);

export const getCompanyRecruiters = (limit = 10) =>
  apiGet<{
    results: RecruiterProfile[];
    pagination: { next: string | null; previous: string | null };
  }>(`${API_BASE_URL}recruiter-profiles/company-users/`, { params: { limit } });

type JobAssignmentsOverviewParams = {
  search?: string;
  status?: string;
  recruiter_company?: string;
  client_company?: string;
  job_title?: string;
  sort?: string;
  page?: number;
  page_size?: number;
  aggregate_by_company?: boolean;
};

export const getJobAssignmentsOverview = (
  params: JobAssignmentsOverviewParams | URLSearchParams | string = {},
) => {
  const queryParams =
    typeof params === 'string'
      ? new URLSearchParams(params)
      : params instanceof URLSearchParams
        ? params
        : new URLSearchParams();
  if (typeof params === 'string' || params instanceof URLSearchParams) {
    const qs = queryParams.toString();
    return apiGet(
      `${API_BASE_URL}recruiters/job-assignments/${qs ? `?${qs}` : ''}`,
    );
  }
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.recruiter_company)
    queryParams.append('recruiter_company', params.recruiter_company);
  if (params.client_company)
    queryParams.append('client_company', params.client_company);
  if (params.job_title) queryParams.append('job_title', params.job_title);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.page_size) {
    queryParams.append('page_size', params.page_size.toString());
  }
  if (params.aggregate_by_company) {
    queryParams.append('aggregate_by_company', 'true');
  }
  const qs = queryParams.toString();
  return apiGet(
    `${API_BASE_URL}recruiters/job-assignments/${qs ? `?${qs}` : ''}`,
  );
};

export const getJobAssignmentsFilterOptions = () =>
  apiGet(`${API_BASE_URL}recruiters/job-assignments-filters/`);

export type JobAssignmentsFilterCountsResponse = {
  status: { active: number; paused: number };
  recruiter_company: { name: string; count: number }[];
};

export const getJobAssignmentsFilterCounts = (params?: URLSearchParams) => {
  const query = params?.toString();
  const suffix = query ? `?${query}` : '';
  return apiGet<JobAssignmentsFilterCountsResponse>(
    `${API_BASE_URL}recruiters/job-assignments/filter-counts/${suffix}`,
  );
};

export const removeJobAssignment = (assignmentId: string) =>
  apiDelete(`${API_BASE_URL}recruiters/job-assignment/remove/`, {
    params: { assignment_id: assignmentId },
  });

export interface ParsedRecruiterRow {
  row_number: number;
  data: {
    email: string;
    name: string;
    title?: string;
    phone?: string;
  };
  is_valid: boolean;
  errors: Record<string, string[]>;
  validated_data?: {
    email: string;
    name: string;
    title?: string;
    phone?: string;
  };
}

export interface CsvParseResponse {
  success: boolean;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  rows: ParsedRecruiterRow[];
  error?: string;
}

export interface BulkInvitePayload {
  emails: { email: string; name: string }[];
  list_id?: string;
  list_name?: string;
  contract_details?: Partial<CompanyAgreement>;
  send_invitation?: boolean;
  agency_company_id?: string;
  agency_company_name?: string;
}

export interface BulkInviteResult {
  email: string;
  name: string;
  success: boolean;
  errors?: Record<string, string[]>;
  is_new_user?: boolean;
  company_name?: string;
}

interface BulkInviteResponse {
  success: boolean;
  list_id?: string | null;
  list_name?: string | null;
  total: number;
  succeeded: number;
  failed: number;
  new_users: number;
  existing_users: number;
  results: BulkInviteResult[];
}

export interface BulkTaskResponse {
  success: boolean;
  task_id: string;
  detail: string;
}

export interface TaskStatusResponse {
  state: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'RETRY';
  result?: BulkInviteResponse;
  error?: string;
}

export const parseRecruitersCsv = (formData: FormData) =>
  apiPost<CsvParseResponse>(`${API_BASE_URL}recruiters/csv-parse/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const bulkInviteAgency = (data: BulkInvitePayload) =>
  apiPost<BulkTaskResponse>(
    `${API_BASE_URL}recruiters/bulk-invite-agency/`,
    data,
  );

export const getTaskStatus = (taskId: string) =>
  apiGet<TaskStatusResponse>(
    `${API_BASE_URL}recruiters/task-status/${taskId}/`,
  );

export interface CompanyInvitation {
  id: string;
  inviting_company: string;
  inviting_company_name: string;
  inviting_company_image?: string;
  inviting_company_is_client_owned?: boolean;
  client_company_name?: string | null;
  invited_user: string;
  invited_user_email: string;
  invited_user_name: string;
  invited_by?: string;
  invited_by_name?: string;
  role: 'admin' | 'member';
  role_display: string;
  status: 'pending' | 'accepted' | 'declined';
  status_display: string;
  message?: string;
  created_at: string;
  accepted_at?: string;
  declined_at?: string;
}

export interface InviteToCompanyPayload {
  email: string;
  role: 'admin' | 'member';
  message?: string;
  /** Only under All workspaces — one invitation per company. */
  company_ids?: string[];
}

export const inviteToCompany = (data: InviteToCompanyPayload) =>
  apiPost<CompanyInvitation>(
    `${API_BASE_URL}recruiter-company-invitations/invite/`,
    data,
  );

export const removeColleagueFromCompany = (recruiterProfileId: string) =>
  apiPost<{ removed: string[] }>(
    `${API_BASE_URL}recruiter-profiles/${recruiterProfileId}/remove-from-company/`,
    {},
  );

export const getPendingInvitations = () =>
  apiGet<CompanyInvitation[]>(
    `${API_BASE_URL}recruiter-company-invitations/pending/`,
  );

export const acceptInvitation = (invitationId: string) =>
  apiPost<{
    success: boolean;
    message: string;
    company_name: string;
    company_id?: string;
    is_client_owned?: boolean;
  }>(`${API_BASE_URL}recruiter-company-invitations/${invitationId}/accept/`);

export const declineInvitation = (invitationId: string) =>
  apiPost<{ success: boolean; message: string }>(
    `${API_BASE_URL}recruiter-company-invitations/${invitationId}/decline/`,
  );

export interface RecruiterTeamInfo {
  company_id?: string;
  company_name?: string;
  team_size: number;
  is_admin: boolean;
  can_invite: boolean;
  invited_by_client?: string;
  pending_invitations_count: number;
}

export const getRecruiterTeamInfo = () =>
  apiGet<RecruiterTeamInfo>(`${API_BASE_URL}recruiter-profiles/team-info/`);

export const downloadRecruiterCsvTemplate = () => {
  const csvContent =
    'email,name\n' +
    'john@agency.com,John Smith\n' +
    'jane@agency.com,Jane Doe';
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'agency_list_invite_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const getRecruiterLists = () =>
  apiGet<RecruiterListItem[]>(`${API_BASE_URL}recruiter-lists/`);

export const createRecruiterList = (
  data: string | { name: string; description?: string },
) => {
  const payload = typeof data === 'string' ? { name: data } : data;
  return apiPost<RecruiterListItem>(`${API_BASE_URL}recruiter-lists/`, payload);
};

export const updateRecruiterList = (
  id: string,
  data: { name?: string; description?: string },
) => apiPatch<RecruiterListItem>(`${API_BASE_URL}recruiter-lists/${id}/`, data);

export const deleteRecruiterList = (id: string) =>
  apiDelete(`${API_BASE_URL}recruiter-lists/${id}/`);

export const addRecruiterToList = (
  listId: string,
  email: string,
  name?: string,
) =>
  apiPost(`${API_BASE_URL}recruiter-lists/${listId}/add-recruiter/`, {
    email,
    ...(name ? { name } : {}),
  });

export const removeRecruiterFromList = (listId: string, recruiterId: string) =>
  apiPost(`${API_BASE_URL}recruiter-lists/${listId}/remove-recruiter/`, {
    recruiter_id: recruiterId,
  });

export const getJobListAssignments = (jobId: string) =>
  apiGet<JobListAssignment[]>(
    `${API_BASE_URL}job-list-assignments/by-job/${jobId}/`,
  );

export const assignListToJob = (
  jobId: string,
  listId: string,
  scheduledAccessDate?: string,
) =>
  apiPost(`${API_BASE_URL}job-list-assignments/assign/`, {
    job_id: jobId,
    list_id: listId,
    ...(scheduledAccessDate
      ? { scheduled_access_date: scheduledAccessDate }
      : {}),
  });

export const unassignListFromJob = (jobId: string, listId: string) =>
  apiPost(`${API_BASE_URL}job-list-assignments/unassign/`, {
    job_id: jobId,
    list_id: listId,
  });

export const updateScheduledAccessDate = (
  jobId: string,
  recruiterId: string,
  scheduledAccessDate: string | null,
) =>
  apiPatch(`${API_BASE_URL}recruiters/job-assignment/schedule/`, {
    job_id: jobId,
    recruiter_id: recruiterId,
    scheduled_access_date: scheduledAccessDate,
  });
